import React, { useState, useEffect, useCallback } from 'react';
import UIModal from '@/components/shared/UIModal';
import UITenantInfo from '@/components/shared/UITenantInfo';
import { usePayments } from '@/hooks/usePayments';
import './DebtorDetailsModal.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

export interface OverduePayment {
  id: string;
  paymentReference: string;
  amount: string;
  currency: string;
  status: string;
  type: string;
  description: string;
  dueDate: string;
  createdAt: string;
}

export interface DebtorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

const baseClass = 'DebtorDetailsModal';

const DebtorDetailsModal: React.FC<DebtorDetailsModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 tenantId,
                                                               }) => {
  const [debtorDetails, setDebtorDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { getDebtors } = usePayments();

  const fetchDebtorDetails = useCallback(async () => {
    if (!tenantId || !isOpen) return;

    try {
      setLoading(true);
      const response = await getDebtors();
      if (response) {
        // Знаходимо конкретного боржника по tenantId
        const debtor = response.find(
          (d: any) => d.tenant?.id?.toString() === tenantId,
        );

        if (debtor) {
          setDebtorDetails(debtor);
        } else {
          console.error('Debtor not found');
        }
      }
    } catch (error) {
      console.error('Error fetching debtor details:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, isOpen, getDebtors]);

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchDebtorDetails();
    } else {
      setDebtorDetails(null);
    }
  }, [isOpen, tenantId, fetchDebtorDetails]);

  const formatCurrency = (amount: string, currency: string) => {
    const symbol = currency === 'USD' ? '$' : currency;
    return `${symbol}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTenantName = () => {
    if (!debtorDetails?.tenant) return 'Unknown Tenant';

    const { name, firstname, lastname } = debtorDetails.tenant;
    if (name) return name;
    if (firstname && lastname) return `${firstname} ${lastname}`;
    return 'Unknown Tenant';
  };

  const getTotalDebt = () => {
    if (!debtorDetails?.overduePayments) return 0;

    return debtorDetails.overduePayments.reduce((total: number, payment: any) => {
      return total + parseFloat(payment.amount || '0');
    }, 0);
  };

  const getStatusColor = (status: string) => {
    switch (safeToLowerCase(status)) {
      case 'pending':
        return '#FF6B35';
      case 'overdue':
        return '#DC3545';
      case 'failed':
        return '#DC3545';
      default:
        return '#6C757D';
    }
  };

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Debtor Details"
      size="large"
      className={baseClass}
    >
      <div className={`${baseClass}__content`}>
        {loading ? (
          <div className={`${baseClass}__loading`}>
            Loading debtor details...
          </div>
        ) : debtorDetails ? (
          <>
            {/* Інформація про орендаря */}
            <div className={`${baseClass}__tenant-info`}>
              <div className={`${baseClass}__tenant-header`}>
                <UITenantInfo
                  name={getTenantName()}
                  email={debtorDetails.tenant.email}
                  tenantId={debtorDetails.tenant.id}
                  avatar={debtorDetails.tenant.avatar}
                  size="large"
                  className={`${baseClass}__tenant-component`}
                />

                <div className={`${baseClass}__debt-summary`}>
                  <div className={`${baseClass}__total-debt`}>
                    <span className={`${baseClass}__debt-label`}>
                      Total Debt:
                    </span>
                    <span className={`${baseClass}__debt-amount`}>
                      {formatCurrency(
                        getTotalDebt().toString(),
                        debtorDetails.overduePayments[0]?.currency || 'USD',
                      )}
                    </span>
                  </div>
                  <div className={`${baseClass}__payments-count`}>
                    {debtorDetails.overduePayments?.length || 0} overdue payment(s)
                  </div>
                </div>
              </div>

              <div className={`${baseClass}__property-info`}>
                <span className={`${baseClass}__property-label`}>Property:</span>
                <span className={`${baseClass}__property-value`}>
                  {debtorDetails.property.name} - Unit {debtorDetails.unit.number}
                </span>
              </div>
            </div>

            {/* Список прострочених платежів */}
            <div className={`${baseClass}__payments-section`}>
              <h4 className={`${baseClass}__payments-title`}>
                Overdue Payments
              </h4>

              <div className={`${baseClass}__payments-list`}>
                {debtorDetails.overduePayments?.map((payment: any) => (
                  <div
                    key={payment.id}
                    className={`${baseClass}__payment-item`}
                  >
                    <div className={`${baseClass}__payment-header`}>
                      <div className={`${baseClass}__payment-info`}>
                        <span className={`${baseClass}__payment-reference`}>
                          {payment.paymentReference}
                        </span>
                        <span
                          className={`${baseClass}__payment-status`}
                          style={{ color: getStatusColor(payment.status) }}
                        >
                          {payment.status.toUpperCase()}
                        </span>
                      </div>
                      <div className={`${baseClass}__payment-amount`}>
                        {formatCurrency(payment.amount, payment.currency)}
                      </div>
                    </div>

                    <div className={`${baseClass}__payment-details`}>
                      <div className={`${baseClass}__payment-description`}>
                        {payment.description}
                      </div>
                      <div className={`${baseClass}__payment-dates`}>
                        <span>Due: {formatDate(payment.dueDate)}</span>
                        <span>Created: {formatDate(payment.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className={`${baseClass}__error`}>
            Unable to load debtor details
          </div>
        )}
      </div>
    </UIModal>
  );
};

export default DebtorDetailsModal;